import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { usePopper } from 'react-popper';

import { getItemFromLocalStorage, setItemInLocalStorage } from '@/utility/localstorage';

import css from './PriceTransparencyPopover.module.css';

const STORAGE_KEY = 'price-transparency-popover-shown';

export const PriceTransparencyPopover = () => {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const [showNotification, setShowNotification] = useState(false);

  // Check if notification has been shown before
  useEffect(() => {
    const hasBeenShown = Boolean(getItemFromLocalStorage(STORAGE_KEY));
    if (!hasBeenShown) {
      const timer = setTimeout(() => {
        setShowNotification(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  const popperOptions = {
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
          padding: 10,
        },
      },
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: ['bottom-start'],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false,
          altAxis: false,
        },
      },
    ],
    placement: 'bottom-start' as const,
  };

  const { styles: popperStyles, attributes } = usePopper(containerEl, popperElement, popperOptions);

  const handleClose = useCallback(() => {
    setShowNotification(false);
    setItemInLocalStorage(STORAGE_KEY, 'true');
  }, []);

  // Early return if notification shouldn't be shown
  if (!showNotification) return null;

  return (
    <>
      <div ref={setContainerEl} />
      {createPortal(
        <div
          ref={setPopperElement}
          style={{ ...popperStyles.popper, zIndex: 5 }}
          {...attributes.popper}>
          <div className="p-4 text-white bg-gray-800 rounded-lg w-fit max-w-[258px]">
            <div ref={setArrowElement} className={css.arrow} />
            <div className="flex items-center gap-2">
              <Text
                variant={ETextStyleVariant.Label}
                className="text-white rounded-lg p-[6px] bg-[#B95B46]">
                <FormattedMessage defaultMessage="NEW" id="5Jcaq4" />
              </Text>
              <Text variant={ETextStyleVariant.SmallRegular}>
                <FormattedMessage defaultMessage="Price Transparency" id="Kfg+5c" />
              </Text>
            </div>
            <div>
              <Text className="py-2" variant={ETextStyleVariant.LegalRegular}>
                <FormattedMessage
                  defaultMessage="Estimated price includes all fees and insurance. Tax will be added at checkout."
                  id="pubeDk"
                />
              </Text>
            </div>
            <div>
              <Text
                variant={ETextStyleVariant.LegalLink}
                onClick={handleClose}
                className="cursor-pointer w-fit">
                <FormattedMessage defaultMessage="Got it" id="NYTGIb" />
              </Text>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};
